import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, FormControlLabel, Switch, TextField, Button,
    FormControl, InputLabel, Select, MenuItem, Divider, Alert
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { AccountService } from '../../services/account.service';
import { useToast } from '../../context/ToastContext';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { useLoanAccounting } from '../../hooks/useLoanAccounting';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { IPayrollSettings } from '../../interfaces/payroll.interface';
import SearchableSelect from '../common/SearchableSelect';
import { helpWhy } from '../../help/why';
import { leafAccounts, depositAccountOptions } from '../../utils/postingAccounts';

const PayrollSettings: React.FC = () => {
    const [deductForAbsence, setDeductForAbsence] = useState(false);
    const [workingDaysPerMonth, setWorkingDaysPerMonth] = useState(26);
    const [autoPostJournalEntry, setAutoPostJournalEntry] = useState(false);
    const [salaryExpenseAccountId, setSalaryExpenseAccountId] = useState<string>('');
    const [cashAccountId, setCashAccountId] = useState<string>('');
    const [bankAccountId, setBankAccountId] = useState<string>('');
    const [fcAccountWarning, setFcAccountWarning] = useState<string | null>(null); // B4-0 (0-2)
    const [taxPayableAccountId, setTaxPayableAccountId] = useState<string>('');
    const [otherDeductionsPayableAccountId, setOtherDeductionsPayableAccountId] = useState<string>('');
    const [salariesPayableAccountId, setSalariesPayableAccountId] = useState<string>('');
    // W3-D employee loans
    const [employeeLoanReceivableAccountId, setEmployeeLoanReceivableAccountId] = useState<string>('');
    const [employeeLoanWriteOffAccountId, setEmployeeLoanWriteOffAccountId] = useState<string>('');
    const [salaryAdvanceMaxPct, setSalaryAdvanceMaxPct] = useState<number | ''>('');
    const [overtimePayEnabled, setOvertimePayEnabled] = useState(false);
    const [overtimeMultiplier, setOvertimeMultiplier] = useState(2);
    const [standardHoursPerDay, setStandardHoursPerDay] = useState(8);
    // S4-13: institution daily OT cap, shown in hours (empty = no cap), and the 48/60-hour week warning.
    const [otDailyCapHours, setOtDailyCapHours] = useState<number | ''>('');
    const [otHoursWarningEnabled, setOtHoursWarningEnabled] = useState(false);
    const [otBaseDesignated, setOtBaseDesignated] = useState(false);
    const [otEarningDesignated, setOtEarningDesignated] = useState(false);
    const [fixedOtEmployeeCount, setFixedOtEmployeeCount] = useState(0);
    const [fixedOtCountAvailable, setFixedOtCountAvailable] = useState(true);
    // T3b (piece-rate garments completion plan, 2026-09-09).
    const [pieceRateEnabled, setPieceRateEnabled] = useState(false);
    const [pieceRateEarningDesignated, setPieceRateEarningDesignated] = useState(false);
    const [pieceRateColdStartCount, setPieceRateColdStartCount] = useState(0);
    const [pieceRateColdStartCountAvailable, setPieceRateColdStartCountAvailable] = useState(true);
    const [attendanceBonusEnabled, setAttendanceBonusEnabled] = useState(false);
    const [attendanceBonusAmount, setAttendanceBonusAmount] = useState<number | ''>('');
    const [attendanceBonusMaxAbsences, setAttendanceBonusMaxAbsences] = useState(0);
    const [gratuityEnabled, setGratuityEnabled] = useState(false);
    const [gratuityDaysPerServiceYear, setGratuityDaysPerServiceYear] = useState<number | ''>(30);
    const [payslipEmailMode, setPayslipEmailMode] = useState<'MANUAL' | 'AUTO_ON_FINALIZE'>('MANUAL');
    // Bangla payslip (2026-09-11): institution-level payslip render language.
    const [payslipLanguage, setPayslipLanguage] = useState<'EN' | 'BN'>('EN');
    const [expenseAccounts, setExpenseAccounts] = useState<Account[]>([]);
    const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);
    const [liabilityAccounts, setLiabilityAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();
    // Without the Accounting module payroll keeps no books: hide every account setting.
    const { accountingOn } = useLoanAccounting(institutionId);

    useEffect(() => {
        if (!institutionId) return;
        loadSettings();
        loadAccounts();
        loadOvertimeConfig();
    }, [institutionId]);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const data: IPayrollSettings | null = await PayrollService.getPayrollSettings(institutionId);
            if (data) {
                setDeductForAbsence(!!data.deductForAbsence);
                setWorkingDaysPerMonth(data.workingDaysPerMonth ?? 26);
                setAutoPostJournalEntry(!!data.autoPostJournalEntry);
                setSalaryExpenseAccountId(data.salaryExpenseAccountId || '');
                setCashAccountId(data.cashAccountId || '');
                setBankAccountId(data.bankAccountId || '');
                setFcAccountWarning(data.foreignCurrencyAccountWarning || null); // B4-0 (0-2)
                setTaxPayableAccountId(data.taxPayableAccountId || '');
                setOtherDeductionsPayableAccountId(data.otherDeductionsPayableAccountId || '');
                setSalariesPayableAccountId(data.salariesPayableAccountId || '');
                setEmployeeLoanReceivableAccountId(data.employeeLoanReceivableAccountId || ''); // W3-D
                setEmployeeLoanWriteOffAccountId(data.employeeLoanWriteOffAccountId || ''); // W3-D
                setSalaryAdvanceMaxPct(data.salaryAdvanceMaxPct ? data.salaryAdvanceMaxPct : ''); // W3-D
                setOvertimePayEnabled(!!data.overtimePayEnabled);
                setOvertimeMultiplier(data.overtimeMultiplier ?? 2);
                setStandardHoursPerDay(data.standardHoursPerDay ?? 8);
                setOtDailyCapHours(data.otDailyCapMinutes ? data.otDailyCapMinutes / 60 : '');
                setOtHoursWarningEnabled(!!data.otHoursWarningEnabled);
                setPayslipEmailMode(data.payslipEmailMode ?? 'MANUAL');
                setPayslipLanguage(data.payslipLanguage ?? 'EN');
                setAttendanceBonusEnabled(!!data.attendanceBonusEnabled);
                setAttendanceBonusAmount(data.attendanceBonusAmount ?? '');
                setAttendanceBonusMaxAbsences(data.attendanceBonusMaxAbsences ?? 0);
                setGratuityEnabled(!!data.gratuityEnabled);
                setGratuityDaysPerServiceYear(data.gratuityDaysPerServiceYear ?? 30);
                setPieceRateEnabled(!!data.pieceRateEnabled);
            }
            // A `null`/absent response means the institution genuinely has no settings row
            // yet — hard-coded defaults + an enabled Save are correct there, same as today.
            setLoadFailed(false);
        } catch (error) {
            showApiError(error, 'Failed to load payroll settings');
            // Unlike the "no settings row yet" case above, a thrown request means we don't
            // actually know the tenant's real settings. Rendering hard-coded defaults with
            // Save enabled would let a save silently clobber real values (e.g. flip
            // AUTO_ON_FINALIZE back to MANUAL, null out account mappings) — so block Save
            // until a load succeeds.
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    };

    // The designation flags (which drive the toggle's enabled/disabled state) and the
    // fixed-overtime-employee count (which only drives an advisory warning) are fetched
    // independently and must not share a failure mode: a broken count endpoint must never
    // leave the toggle permanently — and misleadingly — disabled.
    const loadOvertimeConfig = async () => {
        const [componentsResult, countResult, pieceRateCountResult] = await Promise.allSettled([
            PayrollService.getSalaryComponents(institutionId),
            PayrollService.getOvertimeFixedCount(institutionId),
            PayrollService.getPieceRateColdStartCount(institutionId),
        ]);

        if (componentsResult.status === 'fulfilled') {
            const components = Array.isArray(componentsResult.value) ? componentsResult.value : [];
            setOtBaseDesignated(components.some((c) => c.isOvertimeBase));
            setOtEarningDesignated(components.some((c) => c.isOvertimeEarning));
            // T3b: mirrors PayrollSettingsService#assertPieceRateComponentDesignated -- never
            // rely on the 400, pre-disable the toggle client-side instead.
            setPieceRateEarningDesignated(components.some((c) => c.isPieceRateEarning));
        } else {
            showApiError(componentsResult.reason, 'Failed to load overtime configuration');
        }

        if (countResult.status === 'fulfilled' && typeof countResult.value === 'number') {
            setFixedOtEmployeeCount(countResult.value);
            setFixedOtCountAvailable(true);
        } else {
            // Best-effort only: fall back to a generic (un-numbered) warning instead of
            // blocking or misrepresenting the toggle state.
            setFixedOtCountAvailable(false);
        }

        // T3b (N6): same best-effort discipline as the overtime count above -- a broken count
        // endpoint must never block or misrepresent the piece-rate toggle itself.
        if (pieceRateCountResult.status === 'fulfilled' && typeof pieceRateCountResult.value === 'number') {
            setPieceRateColdStartCount(pieceRateCountResult.value);
            setPieceRateColdStartCountAvailable(true);
        } else {
            setPieceRateColdStartCountAvailable(false);
        }
    };

    // Cash/bank pickers: cash-class posting accounts only (all posting assets if the chart has
    // none tagged). A previously saved account stays selectable so it doesn't silently blank out.
    const cashBankOptions = (selectedId: string): Account[] => {
        // B4-0 (0-2): salary payments are BDT, so foreign-currency accounts are not offered.
        const options = depositAccountOptions(assetAccounts).filter(a => !a.foreignCurrencyCode);
        const saved = selectedId ? assetAccounts.find(a => a.id === selectedId) : undefined;
        return saved && !options.some(a => a.id === saved.id) ? [...options, saved] : options;
    };

    const loadAccounts = async () => {
        try {
            const [expenses, assets, liabilities] = await Promise.all([
                AccountService.getAccountsByType('EXPENSE'),
                AccountService.getAccountsByType('ASSET'),
                AccountService.getAccountsByType('LIABILITY'),
            ]);
            setExpenseAccounts(Array.isArray(expenses) ? leafAccounts(expenses) : []);
            setAssetAccounts(Array.isArray(assets) ? leafAccounts(assets) : []);
            setLiabilityAccounts(Array.isArray(liabilities) ? leafAccounts(liabilities) : []);
        } catch (error) {
            showApiError(error, 'Failed to load chart of accounts');
        }
    };

    // Mirrors PayrollSettingsService: working days per month must be 20 to 31.
    const workingDaysValid = workingDaysPerMonth >= 20 && workingDaysPerMonth <= 31;

    const handleSave = async () => {
        try {
            const payload: Partial<IPayrollSettings> = {
                deductForAbsence,
                workingDaysPerMonth,
                autoPostJournalEntry,
                payslipEmailMode,
                payslipLanguage,
                salaryExpenseAccountId: salaryExpenseAccountId || null,
                cashAccountId: cashAccountId || null,
                bankAccountId: bankAccountId || null,
                taxPayableAccountId: taxPayableAccountId || null,
                otherDeductionsPayableAccountId: otherDeductionsPayableAccountId || null,
                salariesPayableAccountId: salariesPayableAccountId || null,
                // W3-D employee loan fields.
                // null = unchanged on the server, so an emptied picker must say so explicitly, and an empty cap is 0.
                employeeLoanReceivableAccountId: employeeLoanReceivableAccountId || undefined,
                employeeLoanWriteOffAccountId: employeeLoanWriteOffAccountId || undefined,
                clearLoanAccounts: accountingOn && (!employeeLoanReceivableAccountId || !employeeLoanWriteOffAccountId),
                salaryAdvanceMaxPct: salaryAdvanceMaxPct === '' ? 0 : salaryAdvanceMaxPct,
                overtimePayEnabled,
                overtimeMultiplier,
                standardHoursPerDay,
                otDailyCapMinutes: otDailyCapHours === '' ? 0 : Math.round(otDailyCapHours * 60),
                otHoursWarningEnabled,
                attendanceBonusEnabled,
                attendanceBonusAmount: attendanceBonusAmount === '' ? null : attendanceBonusAmount,
                attendanceBonusMaxAbsences,
                gratuityEnabled,
                gratuityDaysPerServiceYear: gratuityDaysPerServiceYear === '' ? null : gratuityDaysPerServiceYear,
                pieceRateEnabled,
            };
            await PayrollService.updatePayrollSettings(payload, institutionId);
            showSuccess('Payroll settings saved');
        } catch (error) {
            showApiError(error, 'Failed to save settings');
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><Typography>Loading...</Typography></Box>;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Payroll Settings</Typography>
                {isSystemAdmin && (
                    <SearchableSelect
                        data-help="payroll.settings.field.institution" label="Select Institution"
                        options={institutions}
                        value={institutionId}
                        onChange={id => setInstitutionId(id)}
                        getOptionLabel={inst => inst.name}
                        clearable={false}
                        sx={{ minWidth: 220 }}
                    />
                )}
            </Box>

            <Paper data-help-page="payroll.settings.page" sx={{ p: 3, maxWidth: 600 }}>
                {loadFailed && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        action={
                            <Button color="inherit" size="small" onClick={loadSettings}>
                                Retry
                            </Button>
                        }
                    >
                        Failed to load payroll settings. The form below may not reflect the
                        actual saved configuration — saving is disabled until the load succeeds.
                    </Alert>
                )}

                {fcAccountWarning && (
                    <Alert severity="warning" sx={{ mb: 3 }} data-testid="payroll-fc-account-warning">
                        {fcAccountWarning}
                    </Alert>
                )}

                <FormControlLabel
                    control={
                        <Switch
                            checked={deductForAbsence}
                            onChange={(e) => setDeductForAbsence(e.target.checked)}
                        />
                    }
                    data-help="payroll.settings.field.deductForAbsence" label="Deduct salary for absence (no attendance record)"
                    sx={{ mb: 3, display: 'block' }}
                />

                <TextField
                    data-help="payroll.settings.field.workingDays" label="Working Days Per Month"
                    type="number"
                    value={workingDaysPerMonth}
                    onChange={(e) => setWorkingDaysPerMonth(parseInt(e.target.value) || 26)}
                    inputProps={{ min: 20, max: 31 }}
                    error={!workingDaysValid}
                    helperText={workingDaysValid ? 'Used to calculate daily rate for deductions' : 'Must be from 20 to 31'}
                    fullWidth
                    sx={{ mb: 3 }}
                />

                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="payslip-email-mode-label">Payslip Email Mode</InputLabel>
                    <Select
                        data-help="payroll.settings.field.payslipEmail" labelId="payslip-email-mode-label"
                        label="Payslip Email Mode"
                        value={payslipEmailMode}
                        onChange={(e) => setPayslipEmailMode(e.target.value as 'MANUAL' | 'AUTO_ON_FINALIZE')}
                        inputProps={{ 'aria-label': 'Payslip Email Mode' }}
                    >
                        <MenuItem value="MANUAL">Manual (send from the payroll screen)</MenuItem>
                        <MenuItem value="AUTO_ON_FINALIZE">Auto-send on finalize</MenuItem>
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        AUTO sends each employee their payslip automatically when payroll is finalized.
                        Manual / resend buttons stay available either way.
                    </Typography>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="payslip-language-label">Payslip Language</InputLabel>
                    <Select
                        data-help="payroll.settings.field.payslipLanguage" labelId="payslip-language-label"
                        label="Payslip Language"
                        value={payslipLanguage}
                        onChange={(e) => setPayslipLanguage(e.target.value as 'EN' | 'BN')}
                        inputProps={{ 'aria-label': 'Payslip Language' }}
                    >
                        <MenuItem value="EN">English</MenuItem>
                        <MenuItem value="BN">বাংলা</MenuItem>
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Controls the language of the payslip PDF only (single download, ZIP,
                        combined PDF, and e-mail). No other report is affected.
                    </Typography>
                </FormControl>

                {accountingOn && (<>
                <Divider sx={{ my: 3 }} />
                <Typography data-help-section="payroll.settings.section.accounting" variant="h6" sx={{ mb: 2 }}>Accounting</Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={autoPostJournalEntry}
                            onChange={(e) => setAutoPostJournalEntry(e.target.checked)}
                        />
                    }
                    data-help="payroll.settings.field.autoPost" label="Auto-post salary payments to journal entries"
                    sx={{ mb: 2, display: 'block' }}
                />

                {autoPostJournalEntry && (
                    <>
                        {(expenseAccounts.length === 0 && assetAccounts.length === 0) && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                No chart-of-account entries found. Create accounts first; leaving
                                these empty means payments will not be posted to the books automatically and will need to be reconciled by hand.
                            </Alert>
                        )}

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <AccountAutocomplete
                                data-help="payroll.settings.field.expenseAccount" label="Salary Expense Account"
                                accounts={expenseAccounts}
                                value={salaryExpenseAccountId}
                                onChange={setSalaryExpenseAccountId}
                                placeholder="None"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                Required to post any payment to the books automatically. If left empty, payments are not posted and must be reconciled by hand.
                            </Typography>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <AccountAutocomplete
                                data-help="payroll.settings.field.cashAccount" label="Cash Account"
                                accounts={cashBankOptions(cashAccountId)}
                                value={cashAccountId}
                                onChange={setCashAccountId}
                                placeholder="None"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                Required to post cash payments automatically. If left empty, cash payments are not posted and must be reconciled by hand.
                            </Typography>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <AccountAutocomplete
                                data-help="payroll.settings.field.bankAccount" label="Bank Account"
                                accounts={cashBankOptions(bankAccountId)}
                                value={bankAccountId}
                                onChange={setBankAccountId}
                                placeholder="None"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                Required to post bank, mobile banking and cheque payments automatically.
                                If left empty, those payments are not posted and must be reconciled by hand.
                            </Typography>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <AccountAutocomplete
                                data-help="payroll.settings.field.taxAccount" label="Tax Payable Account (TDS)"
                                accounts={liabilityAccounts}
                                value={taxPayableAccountId}
                                onChange={setTaxPayableAccountId}
                                placeholder="None"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                Required when payroll deducts income tax. JE will Cr this account for the
                                withheld amount. If left empty while tax is deducted, the payment is not posted automatically.
                            </Typography>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <AccountAutocomplete
                                data-help="payroll.settings.field.otherDeductionsAccount" label="Other Deductions Payable Account"
                                accounts={liabilityAccounts}
                                value={otherDeductionsPayableAccountId}
                                onChange={setOtherDeductionsPayableAccountId}
                                placeholder="None"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                Credited with non-tax deductions (Provident Fund, loan or advance recovery, fines...).
                                If left empty while a payroll has such deductions, the payment is not posted automatically.
                            </Typography>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <AccountAutocomplete
                                data-help="payroll.settings.field.salariesPayableAccount" label="Salaries Payable Account"
                                accounts={liabilityAccounts}
                                value={salariesPayableAccountId}
                                onChange={setSalariesPayableAccountId}
                                placeholder="None"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                Finalizing a payroll books the salary expense and credits net pay here; paying the
                                salary then clears it against cash/bank. If left empty, finalize still works but the
                                expense is only recognised when the salary is paid, and the row is flagged "Accrual not posted".
                            </Typography>
                        </FormControl>
                    </>
                )}
                </>)}

                {/* W3-D employee loans and salary advances */}
                <Divider sx={{ my: 3 }} />
                <Typography data-help-section="payroll.settings.section.loans" variant="h6" sx={{ mb: 2 }}>Employee loans and advances</Typography>
                {!accountingOn && (
                    <Alert severity="info" sx={{ mb: 3 }} data-testid="payroll-loans-register-note">
                        The Accounting module is off, so loans and advances are kept as a simple register: payouts,
                        payroll recoveries and repayments are recorded with no journal entries. If Accounting is
                        switched on later, the open balances are brought into the books once from the Employee Loans page.
                    </Alert>
                )}
                {accountingOn && (<>
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <AccountAutocomplete
                        data-help="payroll.settings.field.loanReceivableAccount" label="Employee Loan Receivable Account"
                        accounts={assetAccounts}
                        value={employeeLoanReceivableAccountId}
                        onChange={setEmployeeLoanReceivableAccountId}
                        placeholder="None"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        An asset account. Debited when a loan or advance is paid out, credited as it is recovered through payroll,
                        cash repayment or final settlement. Required before any loan can be disbursed.
                    </Typography>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <AccountAutocomplete
                        data-help="payroll.settings.field.loanWriteOffAccount" label="Employee Loan Write-off Account"
                        accounts={expenseAccounts}
                        value={employeeLoanWriteOffAccountId}
                        onChange={setEmployeeLoanWriteOffAccountId}
                        placeholder="None"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        An expense account debited when an unrecoverable loan balance is written off. Loan recovery taken in a
                        final settlement is booked against Salaries Payable (it reduces the amount payable to the leaver).
                    </Typography>
                </FormControl>
                </>)}
                <TextField
                    data-help="payroll.settings.field.advanceCap" label="Salary advance cap (% of last gross salary)"
                    type="number"
                    value={salaryAdvanceMaxPct}
                    onChange={(e) => setSalaryAdvanceMaxPct(e.target.value === '' ? '' : parseFloat(e.target.value))}
                    inputProps={{ min: 0.01, max: 100, step: '0.01' }}
                    helperText="Optional. A salary advance request above this share of the employee's last finalized gross (or salary structure) is refused. Leave empty for no cap."
                    fullWidth
                    sx={{ mb: 3 }}
                />

                <Divider sx={{ my: 3 }} />
                <Typography data-help-section="payroll.settings.section.overtime" variant="h6" sx={{ mb: 2 }}>Overtime</Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={overtimePayEnabled}
                            onChange={(e) => setOvertimePayEnabled(e.target.checked)}
                            disabled={!otBaseDesignated || !otEarningDesignated}
                            inputProps={{ 'aria-label': 'Pay computed overtime' }}
                        />
                    }
                    data-help="payroll.settings.field.otEnabled" label="Pay computed overtime"
                    sx={{ display: 'block' }}
                />

                {(!otBaseDesignated || !otEarningDesignated) && (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        Designate an overtime base and an overtime earning component on the
                        Salary Components screen first.
                    </Typography>
                )}

                {overtimePayEnabled && fixedOtCountAvailable && fixedOtEmployeeCount > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        {fixedOtEmployeeCount} employees have a fixed overtime amount. Enabling computed
                        overtime replaces those amounts — they will no longer be paid.
                    </Alert>
                )}

                {overtimePayEnabled && !fixedOtCountAvailable && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Enabling computed overtime may replace existing fixed overtime amounts on
                        some employees — they would no longer be paid the fixed amount. (Could not
                        confirm how many employees are affected.)
                    </Alert>
                )}

                <TextField
                    data-help="payroll.settings.field.otMultiplier" label="Overtime Multiplier"
                    type="number"
                    value={overtimeMultiplier}
                    onChange={(e) => setOvertimeMultiplier(Number(e.target.value))}
                    disabled={!overtimePayEnabled}
                    helperText="Bangladesh Labour Act 2006 §108 requires 2×"
                    inputProps={{ min: 0.01, max: 10, step: 0.25 }}
                    sx={{ mt: 2, mr: 2 }}
                />

                <TextField
                    data-help="payroll.settings.field.standardHours" label="Standard Hours Per Day"
                    type="number"
                    value={standardHoursPerDay}
                    onChange={(e) => setStandardHoursPerDay(Number(e.target.value))}
                    disabled={!overtimePayEnabled}
                    helperText="Used as the divisor when deriving the hourly rate"
                    inputProps={{ min: 0.01, max: 24, step: 0.5 }}
                    sx={{ mt: 2, mb: 3, display: 'block' }}
                />

                <TextField
                    data-help="payroll.settings.field.otDailyCap" label="Daily overtime cap (hours)"
                    type="number"
                    value={otDailyCapHours}
                    onChange={(e) => setOtDailyCapHours(e.target.value === '' ? '' : Number(e.target.value))}
                    helperText="Most overtime counted per day. Empty = no company cap (a shift's own cap still applies)"
                    inputProps={{ min: 0, max: 12, step: 0.5 }}
                    sx={{ mb: 2, display: 'block' }}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={otHoursWarningEnabled}
                            onChange={(e) => setOtHoursWarningEnabled(e.target.checked)}
                            inputProps={{ 'aria-label': 'Warn about 48/60-hour weeks' }}
                        />
                    }
                    data-help="payroll.settings.field.otHoursWarning" label="Warn about 48/60-hour weeks"
                    sx={{ display: 'block', mb: 1 }}
                />

                <Divider sx={{ my: 3 }} />
                <Typography data-help-section="payroll.settings.section.attendanceBonus" variant="h6" sx={{ mb: 2 }}>Attendance bonus</Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={attendanceBonusEnabled}
                            onChange={(e) => setAttendanceBonusEnabled(e.target.checked)}
                            inputProps={{ 'aria-label': 'Pay attendance bonus' }}
                        />
                    }
                    data-help="payroll.settings.field.bonusEnabled" label="Pay attendance bonus"
                    sx={{ display: 'block' }}
                />

                <TextField
                    data-help="payroll.settings.field.bonusAmount" label="Attendance Bonus Amount"
                    type="number"
                    value={attendanceBonusAmount}
                    onChange={(e) => setAttendanceBonusAmount(e.target.value === '' ? '' : Number(e.target.value))}
                    disabled={!attendanceBonusEnabled}
                    helperText="Flat amount paid when the absence threshold below is met"
                    inputProps={{ min: 0.01, step: 0.01 }}
                    sx={{ mt: 2, mr: 2 }}
                />

                <TextField
                    data-help="payroll.settings.field.bonusMaxAbsences" label="Max Absences"
                    type="number"
                    value={attendanceBonusMaxAbsences}
                    onChange={(e) => setAttendanceBonusMaxAbsences(parseInt(e.target.value) || 0)}
                    disabled={!attendanceBonusEnabled}
                    helperText="Absent + approved-leave days must not exceed this to earn the bonus"
                    inputProps={{ min: 0, step: 1 }}
                    sx={{ mt: 2, mb: 3, display: 'block' }}
                />

                <Divider sx={{ my: 3 }} />
                <Typography data-help-section="payroll.settings.section.gratuity" variant="h6" sx={{ mb: 2 }}>Gratuity</Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={gratuityEnabled}
                            onChange={(e) => setGratuityEnabled(e.target.checked)}
                            inputProps={{ 'aria-label': 'Pay gratuity on exit settlement' }}
                        />
                    }
                    data-help="payroll.settings.field.gratuityEnabled" label="Pay gratuity on exit settlement"
                    sx={{ display: 'block' }}
                />

                <TextField
                    data-help="payroll.settings.field.gratuityDays" label="Gratuity Days Per Service Year"
                    type="number"
                    value={gratuityDaysPerServiceYear}
                    onChange={(e) => setGratuityDaysPerServiceYear(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    disabled={!gratuityEnabled}
                    helperText="Days of basic pay per completed year of service (1..90)"
                    inputProps={{ min: 1, max: 90, step: 1 }}
                    sx={{ mt: 2, mb: 3, display: 'block' }}
                />

                <Divider sx={{ my: 3 }} />
                <Typography data-help-section="payroll.settings.section.pieceRate" variant="h6" sx={{ mb: 2 }}>Piece-Rate Pay</Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={pieceRateEnabled}
                            onChange={(e) => setPieceRateEnabled(e.target.checked)}
                            disabled={!pieceRateEarningDesignated}
                            inputProps={{ 'aria-label': 'Pay piece-rate wages' }}
                        />
                    }
                    data-help="payroll.settings.field.pieceRateEnabled" label="Pay piece-rate wages"
                    sx={{ display: 'block' }}
                />

                {!pieceRateEarningDesignated && (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        Designate a piece-rate earning component on the Salary Components screen first.
                    </Typography>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: 0.5, display: 'block' }}>
                    Switching an operator to piece-rate means no festival bonus, EL encashment or
                    final settlement figure until three months of piece-rate wage history exist for
                    them (cold start).
                </Typography>

                {pieceRateEnabled && pieceRateColdStartCountAvailable && pieceRateColdStartCount > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        {pieceRateColdStartCount} piece-rate employees currently have fewer than 3
                        qualifying months of wage history — their festival bonus, EL encashment and
                        final settlement figures are still computed off a partial (or withheld)
                        average.
                    </Alert>
                )}

                {pieceRateEnabled && !pieceRateColdStartCountAvailable && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Some piece-rate employees may still be in the three-month cold-start window.
                        (Could not confirm how many are affected.)
                    </Alert>
                )}

                <Button variant="contained" color="primary" data-help="payroll.settings.save" {...helpWhy(loadFailed ? { kind: 'status', message: 'Settings could not be loaded, so saving is blocked. Press Retry above.' } : !workingDaysValid ? { kind: 'status', message: 'Working days per month must be from 20 to 31.' } : null)} onClick={handleSave} disabled={loadFailed || !workingDaysValid} sx={{ mt: 3 }}>
                    Save Settings
                </Button>
            </Paper>
        </Box>
    );
};

export default PayrollSettings;
